/**
 * Audio Notifications Hook
 * 
 * Provides whistle sounds for workout intervals using Web Audio API.
 * Generates the whistle sound programmatically - no external audio files needed.
 */

import { useCallback, useRef, useEffect, useState } from 'react';

/**
 * Generate a whistle sound using Web Audio API
 * @param {AudioContext} audioContext - The audio context
 * @param {string} type - 'start' for ascending whistle, 'stop' for descending
 * @param {number} volume - Volume level 0-1
 */
function playWhistle(audioContext, type = 'start', volume = 0.5) {
  const now = audioContext.currentTime;
  const duration = 0.4; // 400ms whistle
  
  // Create oscillator for the whistle tone
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  // Whistle frequency sweep
  oscillator.type = 'sine';
  
  if (type === 'start') {
    // Ascending whistle for start
    oscillator.frequency.setValueAtTime(800, now);
    oscillator.frequency.exponentialRampToValueAtTime(1600, now + duration * 0.5);
    oscillator.frequency.exponentialRampToValueAtTime(2000, now + duration);
  } else {
    // Descending whistle for stop
    oscillator.frequency.setValueAtTime(2000, now);
    oscillator.frequency.exponentialRampToValueAtTime(1200, now + duration * 0.5);
    oscillator.frequency.exponentialRampToValueAtTime(600, now + duration);
  }
  
  // Volume envelope
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(volume, now + 0.02); // Quick attack
  gainNode.gain.setValueAtTime(volume, now + duration * 0.7);
  gainNode.gain.linearRampToValueAtTime(0, now + duration); // Fade out
  
  oscillator.start(now);
  oscillator.stop(now + duration);
  
  return oscillator;
}

/**
 * Generate a double whistle (two short blasts)
 */
function playDoubleWhistle(audioContext, volume = 0.5) {
  const now = audioContext.currentTime;
  
  // First whistle
  const osc1 = audioContext.createOscillator();
  const gain1 = audioContext.createGain();
  osc1.connect(gain1);
  gain1.connect(audioContext.destination);
  osc1.type = 'sine';
  osc1.frequency.setValueAtTime(1500, now);
  osc1.frequency.exponentialRampToValueAtTime(2200, now + 0.15);
  gain1.gain.setValueAtTime(0, now);
  gain1.gain.linearRampToValueAtTime(volume, now + 0.01);
  gain1.gain.linearRampToValueAtTime(0, now + 0.15);
  osc1.start(now);
  osc1.stop(now + 0.15);
  
  // Second whistle (slightly delayed)
  const osc2 = audioContext.createOscillator();
  const gain2 = audioContext.createGain();
  osc2.connect(gain2);
  gain2.connect(audioContext.destination);
  osc2.type = 'sine';
  osc2.frequency.setValueAtTime(1500, now + 0.2);
  osc2.frequency.exponentialRampToValueAtTime(2200, now + 0.35);
  gain2.gain.setValueAtTime(0, now + 0.2);
  gain2.gain.linearRampToValueAtTime(volume, now + 0.21);
  gain2.gain.linearRampToValueAtTime(0, now + 0.35);
  osc2.start(now + 0.2);
  osc2.stop(now + 0.35);
}

/**
 * Generate a long whistle for workout end
 */
function playLongWhistle(audioContext, volume = 0.5) {
  const now = audioContext.currentTime;
  const duration = 1.0; // 1 second
  
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(audioContext.destination);
  
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(1800, now);
  oscillator.frequency.setValueAtTime(1800, now + duration * 0.6);
  oscillator.frequency.exponentialRampToValueAtTime(800, now + duration);
  
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(volume, now + 0.02);
  gainNode.gain.setValueAtTime(volume, now + duration * 0.7);
  gainNode.gain.linearRampToValueAtTime(0, now + duration);
  
  oscillator.start(now);
  oscillator.stop(now + duration);
}

/**
 * Hook for audio notifications in workouts
 */
export function useAudioNotifications() {
  const audioContextRef = useRef(null);
  const [isMuted, setIsMuted] = useState(() => {
    return localStorage.getItem('audio_notifications_muted') === 'true';
  });
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('audio_notifications_volume');
    return saved ? parseFloat(saved) : 0.7;
  });

  // Initialize audio context on first user interaction
  const initAudioContext = useCallback(() => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    // Resume if suspended (browsers require user gesture)
    if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  // Play interval start whistle
  const playIntervalStart = useCallback(() => {
    if (isMuted) return;
    try {
      const ctx = initAudioContext();
      playWhistle(ctx, 'start', volume);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [isMuted, volume, initAudioContext]);

  // Play interval end whistle
  const playIntervalEnd = useCallback(() => {
    if (isMuted) return;
    try {
      const ctx = initAudioContext();
      playWhistle(ctx, 'stop', volume);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [isMuted, volume, initAudioContext]);

  // Play double whistle (for attention/warning)
  const playAttention = useCallback(() => {
    if (isMuted) return;
    try {
      const ctx = initAudioContext();
      playDoubleWhistle(ctx, volume);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [isMuted, volume, initAudioContext]);

  // Play workout complete whistle
  const playWorkoutComplete = useCallback(() => {
    if (isMuted) return;
    try {
      const ctx = initAudioContext();
      playLongWhistle(ctx, volume);
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [isMuted, volume, initAudioContext]);

  // Play countdown beep
  const playCountdownBeep = useCallback((isLast = false) => {
    if (isMuted) return;
    try {
      const ctx = initAudioContext();
      const now = ctx.currentTime;
      
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      
      osc.type = 'sine';
      osc.frequency.value = isLast ? 1000 : 600;
      
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(volume * 0.5, now + 0.01);
      gain.gain.linearRampToValueAtTime(0, now + (isLast ? 0.3 : 0.1));
      
      osc.start(now);
      osc.stop(now + (isLast ? 0.3 : 0.1));
    } catch (e) {
      console.warn('Audio playback failed:', e);
    }
  }, [isMuted, volume, initAudioContext]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const newValue = !prev;
      localStorage.setItem('audio_notifications_muted', String(newValue));
      return newValue;
    });
  }, []);

  // Set mute state
  const setMuted = useCallback((muted) => {
    setIsMuted(muted);
    localStorage.setItem('audio_notifications_muted', String(muted));
  }, []);

  // Update volume
  const updateVolume = useCallback((newVolume) => {
    setVolume(newVolume);
    localStorage.setItem('audio_notifications_volume', String(newVolume));
  }, []);

  // Test sound
  const testSound = useCallback(() => {
    try {
      const ctx = initAudioContext();
      playWhistle(ctx, 'start', volume);
    } catch (e) {
      console.warn('Audio test failed:', e);
    }
  }, [volume, initAudioContext]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  return {
    // State
    isMuted,
    volume,
    
    // Sound triggers
    playIntervalStart,
    playIntervalEnd,
    playAttention,
    playWorkoutComplete,
    playCountdownBeep,
    
    // Controls
    toggleMute,
    setMuted,
    updateVolume,
    testSound,
    
    // Initialize (call on user interaction)
    initAudioContext
  };
}

export default useAudioNotifications;
